import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
 
 
import { FaRegEdit, FaRegTrashAlt, FaPlus, FaSearch } from "react-icons/fa";
import TierLevelDialog from "./TierLevelDialog";
import { API_URL } from "../../../../api";
import { 
  CircularProgress, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Typography,
  Box,
  Button
} from "@mui/material";

const TierLevel = () => {
  const token = localStorage.getItem("token");
  const url = API_URL();
  const api = axios.create({
    baseURL: url,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  const [tierLevels, setTierLevels] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const showDialog = () => {
    setEditData(null);
    setOpen(true);
  };

  const handleEdit = (data) => {
    setEditData(data);
    setOpen(true);
  };

  const handleDelete = (event) => {
    api
      .delete("tier-levels/" + event)
      .then(() => {
        Swal.fire({
          title: "Success",
          text: "Successfully Deleted!",
          icon: "success",
          confirmButtonText: "OK",
        });
        getTierLevels();
      })
      .catch((error) => {
        Swal.fire({
          title: "Error",
          text: error?.response?.data?.message || "Error deleting item",
          icon: "error",
          confirmButtonText: "CANCEL",
        });
      });
  };

  const confirmDelete = (id) => {
    Swal.fire({
      title: "Delete Confirmation",
      text: "Are you sure to delete this tier level?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete it!",
      cancelButtonText: "No, Cancel",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
    }).then((result) => {
      if (result.isConfirmed) {
        handleDelete(id);
      }
    });
  };

  const getTierLevels = async () => {
    setIsLoading(true);
    try {
      const res = await api.get(`tier-levels`, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: searchTerm
        }
      });
      const dataList = res.data?.data || [];
      setTierLevels(dataList);
      setTotalCount(res.data?._metadata?.total || dataList.length);
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      getTierLevels();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div className="App">
      <div className="wrapper">
        <div className="content-header pb-2">
          <div className="container-fluid px-3">
            <Typography variant="h5" fontWeight="bold" sx={{ color: "#333", display: "flex", alignItems: "center", gap: 1 }}>
              Tier Level Management
            </Typography>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid px-3">
            <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Search by Level Name..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setPage(0);
                    }}
                    sx={{ width: { xs: '100%', sm: '300px' } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaSearch color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<FaPlus />}
                    onClick={showDialog}
                    sx={{ borderRadius: "8px", textTransform: 'none', px: 3 }}
                  >
                    Create Tier Level
                  </Button>
                </Box>

                <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
                  <Table stickyHeader>
                     <TableHead>
                      <TableRow sx={{ "& th": { backgroundColor: "#f5f5f5", color: "#333", fontWeight: "bold" } }}>
                        <TableCell>#</TableCell>
                        <TableCell>Level Name</TableCell>
                        <TableCell>Level Number</TableCell>
                        <TableCell>Entry Level</TableCell>
                        <TableCell>Entry Min Point</TableCell>
                        <TableCell>Point Range</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="center">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: "center", py: 5 }}>
                            <CircularProgress />
                          </TableCell>
                        </TableRow>
                      ) : tierLevels.length > 0 ? (
                        tierLevels.map((tier, i) => (
                          <TableRow key={tier._id || i} hover>
                            <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                            <TableCell fontWeight="medium">{tier.levelName}</TableCell>
                            <TableCell>{tier.levelNumber}</TableCell>
                            <TableCell>{tier.entryLevel ? "Yes" : "No"}</TableCell>
                            <TableCell>{tier.entryMinPoint}</TableCell>
                            <TableCell>{tier.minPointRange} - {tier.maxPointRange}</TableCell>
                            <TableCell sx={{ maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {tier.description || "-"}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Edit">
                                <IconButton color="primary" onClick={() => handleEdit(tier)} size="small">
                                  <FaRegEdit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton color="error" onClick={() => confirmDelete(tier._id)} size="small">
                                  <FaRegTrashAlt />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                         <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: "center", py: 5, color: "text.secondary" }}>
                            No tier levels found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                      "& p": {
                        marginBottom: 0,
                      },
                    }}
                  />
                </TableContainer>
              </CardContent>
            </Card>

            <TierLevelDialog
              open={open}
              close={() => setOpen(false)}
              getTierLevels={getTierLevels}
              editData={editData}
            />
          </div>
        </section>
      </div>
    </div>
  );
};

export default TierLevel;
